/**
 * Two bots playing each other.
 *
 * Not linked from anywhere — it is a window onto the engine rather than a feature of the
 * site. What it is good for is seeing what the game looks like when both sides are
 * playing reasonably, which is hard to learn from the inside of a game you are losing,
 * and for watching the evaluation move: a number that swings forty points on a quiet move
 * is usually a sign the evaluation is wrong about something.
 *
 * One worker, not two. The bots move strictly in turn, so a second thread would sit idle
 * half the time, and a single worker keeps a transposition table per difficulty rather
 * than one per seat — which is the same table, since an entry is a fact about a position
 * and the side to move is part of the position's hash.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FORMATION_NAMES,
  formationInfo,
  type FormationName,
  formatMove,
  Game,
  type Move,
  type PlayerIndex,
  threatenedKings,
  type Variant,
} from '@gok/engine';
import { levelById, type LevelId, LEVELS } from '@gok/bot';

import { type Thought, useBot } from '../bot/useBot.ts';
import { LIVE, useHistoryKeys } from '../game/history.ts';
import { Board } from '../ui/Board.tsx';
import { MoveList } from '../ui/MoveList.tsx';
import { Reserve } from '../ui/Reserve.tsx';
import { Badge, Button, Field, Panel, Seat, Select } from '../ui/primitives.tsx';

/**
 * Give up after this many plies.
 *
 * Repetition ends most games long before it (§8.1) — the search scores a repeat as a
 * draw, so neither side walks into one while it is ahead — but a bot that is losing will
 * happily shuffle, and nothing else here would ever stop it.
 */
const MAX_PLIES = 400;

/** Long enough that a move reads as a move; the level's own budget does the rest. */
const PACE_MS = 120;

const OUTCOME: Record<string, string> = {
  checkmate: 'Checkmate',
  drawn: 'Draw by agreement',
  repetition: 'Draw by repetition',
};

/** A move and, for the bot moves, what the search thought while finding it. */
interface Entry {
  readonly move: Move;
  /** `null` for the opening walk, which nobody thought about. */
  readonly thought: Thought | null;
}

/**
 * A short random opening, so that successive games are not the same game.
 *
 * Both bots are deterministic given a position — even the evaluation noise is derived
 * from the position's own hash, so it is the same on every run — and from the standard
 * opening they would play out identically forever. `bot/tools/selfplay.ts` does this
 * properly for measurement, with a seeded book and a validity check; here the requirement
 * is the opposite of reproducible, so it is a few lines rather than an import.
 *
 * Passing is excluded from the walk: it is legal, but two in a row would end the game
 * before it started (§8).
 */
function randomOpening(variant: Variant, plies: number): Move[] {
  const game = new Game(variant);
  const moves: Move[] = [];

  for (let i = 0; i < plies; i++) {
    const legal = game.legalMoves().filter((move) => move.type !== 'pass');
    if (game.isOver || legal.length === 0) break;
    const move = legal[Math.floor(Math.random() * legal.length)];
    game.play(move);
    moves.push(move);
  }

  return moves;
}

export function Watch() {
  const [levels, setLevels] = useState<readonly [LevelId, LevelId]>([
    'sharp',
    'sharp',
  ]);
  const [formation, setFormation] = useState<FormationName>('monarchy');
  const [radius, setRadius] = useState(4);
  const [openingPlies, setOpeningPlies] = useState(4);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [running, setRunning] = useState(false);
  /** Moves owed to the "Step" button, which is "run, but only this far". */
  const [stepsLeft, setStepsLeft] = useState(0);
  const [ply, setPly] = useState<number>(LIVE);
  const [failure, setFailure] = useState<string | null>(null);

  const { think, cancel, thinking } = useBot(PACE_MS);

  const variant = useMemo((): Variant => ({
    radius: Math.max(radius, formationInfo(formation).minRadius),
    formation,
    spawnsAvailable: 12,
    timeInitialMs: 600_000,
    timeIncrementMs: 5_000,
    stakes: 0,
  }), [radius, formation]);

  // The game, replayed from its log — the same rule every other screen plays by, and the
  // reason stepping backwards through it is just replaying fewer moves.
  const replay = useMemo(() => {
    const game = new Game(variant);
    const sans: { san: string }[] = [];
    for (const entry of entries) {
      sans.push({ san: formatMove(game.position, entry.move) });
      game.play(entry.move);
    }
    return { game, sans };
  }, [variant, entries]);

  const live = ply >= entries.length;

  const shown = useMemo(() => {
    if (live) {
      return { game: replay.game, lastMove: entries[entries.length - 1]?.move };
    }
    const game = new Game(variant);
    for (let i = 0; i < ply; i++) game.play(entries[i].move);
    return { game, lastMove: entries[ply - 1]?.move };
  }, [live, replay.game, variant, entries, ply]);

  useHistoryKeys({ ply, moveCount: entries.length, seek: setPly });

  const over = replay.game.isOver || entries.length >= MAX_PLIES;
  const botTurn = (running || stepsLeft > 0) && !over;

  useEffect(() => {
    if (!botTurn) return;

    const seat = replay.game.position.playerToMove;
    let abandoned = false;

    think(variant, entries.map((entry) => entry.move), levels[seat])
      .then((thought) => {
        if (abandoned) return;
        setFailure(null);
        setEntries((current) =>
          current.length === entries.length
            ? [...current, { move: thought.move, thought }]
            : current
        );
        setStepsLeft((n) => Math.max(0, n - 1));
      })
      .catch((error: Error) => {
        if (abandoned) return;
        setFailure(error.message);
        setRunning(false);
        setStepsLeft(0);
      });

    return () => {
      abandoned = true;
    };
  }, [botTurn, entries, variant, levels, think, replay.game]);

  const newGame = useCallback(() => {
    cancel();
    setRunning(false);
    setStepsLeft(0);
    setFailure(null);
    setPly(LIVE);
    setEntries(
      randomOpening(variant, openingPlies).map((move) => ({
        move,
        thought: null,
      })),
    );
  }, [cancel, variant, openingPlies]);

  /**
   * Deal a game on arrival, and again whenever a setup control changes.
   *
   * There is no "start" step. There was, gated on the move log being empty — which is
   * wrong in the one case that matters: an opening of no plies leaves an empty log, and
   * an empty log is a game that has not moved yet, not the absence of one. Dealing
   * automatically removes the state that was being inferred rather than fixing the
   * inference.
   *
   * `newGame` changes identity exactly when the variant or the opening length does, so
   * this fires for those and not for a change of difficulty, which both sides are allowed
   * to make mid-game.
   */
  useEffect(() => newGame(), [newGame]);

  const last = [...entries].reverse().find((entry) => entry.thought)?.thought ??
    undefined;
  const toMove = replay.game.position.playerToMove;

  return (
    <div className='flex h-full min-h-0 flex-col lg:flex-row'>
      <div className='relative flex min-h-[40vh] flex-1 items-center justify-center p-2
          sm:p-4 lg:min-h-0'>
        {!live && (
          <button
            type='button'
            onClick={() => setPly(LIVE)}
            className='absolute top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border
              border-alert bg-alert-soft px-3 py-1 text-xs font-medium text-alert shadow-sm'
          >
            Viewing move {ply} of {entries.length} — back to the game
          </button>
        )}

        <div className='h-full w-full'>
          <Board
            board={shown.game.position.board}
            cells={shown.game.position.cells}
            selection={null}
            targets={{ moves: new Map(), captures: new Set(), bows: [] }}
            viewFrom={0}
            toMove={shown.game.position.playerToMove}
            interactive={false}
            lastMove={shown.lastMove}
            threatened={threatenedKings(shown.game.position)}
            onSelect={() => {}}
            onPlay={() => {}}
          />
        </div>
      </div>

      <aside className='flex max-h-[48vh] w-full shrink-0 flex-col gap-2 overflow-y-auto
          border-t border-rule bg-ground p-2 lg:h-full lg:max-h-none lg:w-80 lg:gap-3
          lg:overflow-hidden lg:border-t-0 lg:border-l lg:p-3'>
        <SideRow
          seat={1}
          level={levels[1]}
          spawns={replay.game.position.spawns[1]}
          thinking={thinking && toMove === 1}
          active={!over && toMove === 1}
          onChange={(id) => setLevels(([a]) => [a, id])}
        />

        {over && (
          <div className='rounded-md border border-rule bg-surface px-3 py-2 text-sm'>
            <strong className='font-semibold'>
              {replay.game.isOver
                ? OUTCOME[replay.game.status] ?? replay.game.status
                : 'Stopped'}
            </strong>
            <div className='mt-0.5 text-ink-2'>
              {!replay.game.isOver
                ? `No result after ${MAX_PLIES} plies.`
                : replay.game.winner === undefined
                ? 'A draw.'
                : `${levelById(levels[replay.game.winner]).label} wins as ${
                  replay.game.winner === 0 ? 'blue' : 'red'
                }.`}
            </div>
          </div>
        )}

        {failure && (
          <p className='rounded-md border border-alert bg-alert-soft px-3 py-2 text-sm text-alert'>
            The bot stopped: {failure}
          </p>
        )}

        <Readout thought={last} entries={entries} />

        <Panel title='Moves' className='min-h-24 lg:min-h-40 lg:flex-1'>
          <MoveList moves={replay.sans} ply={live ? -1 : ply} onSeek={setPly} />
        </Panel>

        <SideRow
          seat={0}
          level={levels[0]}
          spawns={replay.game.position.spawns[0]}
          thinking={thinking && toMove === 0}
          active={!over && toMove === 0}
          onChange={(id) => setLevels(([, b]) => [id, b])}
        />

        <div className='flex flex-wrap gap-2'>
          <Button
            tone='primary'
            className='flex-1'
            disabled={over}
            onClick={() => setRunning((value) => !value)}
          >
            {running ? 'Pause' : 'Play'}
          </Button>
          <Button
            className='flex-1'
            disabled={over || running}
            onClick={() => setStepsLeft((n) => n + 1)}
          >
            Step
          </Button>
          <Button className='flex-1' onClick={newGame}>
            New game
          </Button>
        </div>

        <Panel title='Setup' className='shrink-0'>
          <div className='flex flex-col gap-3 p-3'>
            <div className='flex gap-2'>
              <div className='flex-1'>
                <Field label='Formation'>
                  <Select
                    value={formation}
                    onChange={(e) => setFormation(e.target.value as FormationName)}
                  >
                    {FORMATION_NAMES.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className='flex-1'>
                <Field label='Board'>
                  <Select
                    value={variant.radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                  >
                    {[2, 3, 4, 5, 6]
                      .filter((r) => r >= formationInfo(formation).minRadius)
                      .map((r) => <option key={r} value={r}>radius {r}</option>)}
                  </Select>
                </Field>
              </div>
            </div>

            <Field label='Random opening'>
              <Select
                value={openingPlies}
                onChange={(e) => setOpeningPlies(Number(e.target.value))}
              >
                <option value={0}>none — same game every time</option>
                <option value={2}>2 plies</option>
                <option value={4}>4 plies</option>
                <option value={8}>8 plies</option>
              </Select>
            </Field>
            <p className='-mt-1 text-xs text-muted'>
              Both sides are deterministic, so without a random opening every game is the
              same game. The walk is played before either bot starts thinking.
            </p>
          </div>
        </Panel>
      </aside>
    </div>
  );
}

function SideRow({
  seat,
  level,
  spawns,
  thinking,
  active,
  onChange,
}: {
  seat: PlayerIndex;
  level: LevelId;
  spawns: number;
  thinking: boolean;
  active: boolean;
  onChange(level: LevelId): void;
}) {
  return (
    <div
      className={`rounded-md border bg-surface px-3 py-2 transition-colors
        ${active ? 'border-accent' : 'border-rule'}`}
    >
      <div className='flex items-center gap-2'>
        <Seat player={seat} />
        <Select
          value={level}
          onChange={(e) => onChange(e.target.value as LevelId)}
          className='min-w-0 flex-1 py-0.5 text-xs'
        >
          {LEVELS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </Select>
        {thinking && <Badge tone='accent'>thinking</Badge>}
      </div>
      <div className='mt-1.5'>
        <Reserve count={spawns} player={seat} selected={false} />
      </div>
    </div>
  );
}

/**
 * What the last search found.
 *
 * The score is shown from blue's side throughout rather than from whoever just moved, so
 * a swing on the readout is a swing in the game and not just the seat changing.
 */
function Readout({
  thought,
  entries,
}: {
  thought: Thought | undefined;
  entries: readonly Entry[];
}) {
  if (!thought) {
    return (
      <p className='rounded-md border border-rule bg-surface px-3 py-2 text-xs text-muted'>
        Press Play. Neither side has thought about anything yet.
      </p>
    );
  }

  // The thought belongs to whoever was to move when it was made: the entry before the
  // move it produced, so an even-length prefix means blue.
  const index = entries.findIndex((entry) => entry.thought === thought);
  const mover: PlayerIndex = (index % 2) as PlayerIndex;
  const forBlue = mover === 0 ? thought.score : -thought.score;

  const evaluation = thought.mate
    ? (forBlue > 0 ? 'blue #' : 'red #')
    : `${forBlue >= 0 ? '+' : '−'}${(Math.abs(forBlue) / 100).toFixed(2)}`;

  const rate = thought.elapsedMs > 0 ? Math.round(thought.nodes / thought.elapsedMs) : 0;

  return (
    // One row of instruments. A four-column grid with the score spanning two of them put
    // the second row's labels under the wrong columns, which made the panel read as two
    // unrelated readouts instead of one.
    <dl className='grid grid-cols-5 gap-x-1 rounded-md border border-rule bg-surface
      px-2 py-2 text-center font-mono text-[0.68rem]'>
      <Stat label='eval' value={evaluation} />
      <Stat label='depth' value={String(thought.depth)} />
      <Stat label='knodes' value={String(Math.round(thought.nodes / 1000))} />
      <Stat label='kn/s' value={String(rate)} />
      <Stat label='ms' value={String(Math.round(thought.elapsedMs))} />
    </dl>
  );
}

function Stat(
  { label, value, wide = false }: {
    label: string;
    value: string;
    wide?: boolean;
  },
) {
  return (
    <div className={wide ? 'col-span-2 text-left' : ''}>
      <dt className='text-[0.6rem] tracking-wide text-muted uppercase'>
        {label}
      </dt>
      <dd className='tabular text-ink'>{value}</dd>
    </div>
  );
}
